import { useCallback, useEffect, useMemo } from 'react';
import { useStoreDetailsAudits } from '../audits/hooks/useStoreDetailsAudits';
import { useStoreDetailsUsers } from '../users/hooks/useStoreDetailsUsers';
import { useStoreDetailsPage } from './useStoreDetailsPage';
import { getStoreDetailsTabs } from '../utils/storeDetailsTabs';
import { isValidStoreDetailsTab } from '../utils/storeDetailsPageUtils';

export const useStoreDetailsPageLogic = () => {
  const pageState = useStoreDetailsPage();
  const {
    activeTab,
    editDialogOpen,
    error,
    handleCloseEditDialog,
    handleOpenEditDialog,
    handleBack,
    handleSaveStore,
    isSuperAdmin,
    loadStore,
    loadStoreSilently,
    loading,
    navigate,
    saving,
    setActiveTab,
    store,
    storeId,
    todayDate,
  } = pageState;

  const canShowManualLottoSales = store?.has_vending_machine === true;
  const canShowManualPayout = store?.enable_manual_payout_entry === true;
  const hasManualEntryOptions = canShowManualLottoSales || canShowManualPayout;

  const tabs = useMemo(() => getStoreDetailsTabs(hasManualEntryOptions), [hasManualEntryOptions]);

  const handleTabChange = useCallback(
    (tabKey: string) => {
      if (isValidStoreDetailsTab(tabKey)) {
        setActiveTab(tabKey);
      }
    },
    [setActiveTab]
  );

  const auditsState = useStoreDetailsAudits({
    activeTab,
    isSuperAdmin,
    store,
    storeId,
  });

  const usersState = useStoreDetailsUsers({
    isSuperAdmin,
    ownerUserId: store?.owner_user_id,
    storeId,
    activeTab,
  });

  useEffect(() => {
    if (!hasManualEntryOptions && activeTab === 'manual_entries') {
      setActiveTab('overview');
    }
  }, [activeTab, hasManualEntryOptions, setActiveTab]);

  return {
    model: {
      activeTab,
      editDialogOpen,
      error,
      isSuperAdmin,
      loading,
      saving,
      store,
      storeId,
      todayDate,
      tabs,
      auditsState,
      usersState,
    },
    actions: {
      handleCloseEditDialog,
      handleOpenEditDialog,
      handleBack,
      handleSaveStore,
      loadStore,
      loadStoreSilently,
      navigate,
      handleTabChange,
      setActiveTab,
    },
  };
};
